import { useCallback, useEffect, useRef, useState } from 'react';
import type { HexTileCoordinates } from '@/game/hexmap';
import type { Command } from '@/game/sim';
import { cityName } from '@/game/view/cities';
import {
  commandUnitIds,
  droppedFoundOrders,
  foundOrders,
  mergeToasts,
  type PendingCommand,
  settleCommands,
  TOAST_MS,
  type Toast,
  trackCommand,
} from '@/game/view/commandFeedback';
import type { Frame, FrameStore } from '@/game/view/frame';
import { reallyOwns } from '@/game/view/viewerState';
import { commandSpend, sumSpends } from '../hud/spend';

/** How long the resources pill shows what a landed click spent. */
const SPEND_FLASH_MS = 1600;

export type PushToasts = (added: Array<Pick<Toast, 'text' | 'tone'>>) => void;

/** What landed clicks just spent ("−20🌾 −12🪵" under the resources pill). */
export interface SpendFlash {
  id: number;
  amounts: Record<string, number>;
}

/**
 * Refused clicks: commands whose effect should show up are tracked, and a
 * toast says why when it doesn't in time (game/view/commandFeedback.ts); a
 * settler sent to found a city that stops without founding says why too.
 * A tracked command that spends (training, a site, an upgrade, a promotion,
 * a rank) flashes what it spent under the resources pill once it lands: the
 * moment the pill's numbers drop, so a click that took is plain to see.
 * Returns the `dispatch` everything on screen sends commands through.
 */
export function useCommandFeedback(
  frames: FrameStore,
  frame: Frame,
  send: (command: Command) => void
) {
  const pendingRef = useRef<PendingCommand[]>([]);
  // Sites the player cancelled: their removal isn't reported as a loss.
  const [cancelled] = useState(() => new Set<string>());
  const claimedRef = useRef(new Set<string>());
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);
  // What each tracked command spends, by its command (waiting entries may be copies).
  const spendRef = useRef(new WeakMap<Command, Record<string, number>>());
  const [spends, setSpends] = useState<SpendFlash[]>([]);
  const spendIdRef = useRef(0);
  const flashSpend = useCallback((amounts: Record<string, number>) => {
    const id = ++spendIdRef.current;
    setSpends((prev) => [...prev, { id, amounts }]);
    setTimeout(() => setSpends((prev) => prev.filter((f) => f.id !== id)), SPEND_FLASH_MS);
  }, []);
  // A repeat of a toast already up counts on it ("×3") and refreshes its timer.
  const pushToasts: PushToasts = useCallback((added) => {
    setToasts((prev) => mergeToasts(prev, added, Date.now(), TOAST_MS, () => ++toastIdRef.current));
    // Each toast's own `until` decides, so an earlier push's timer leaves a
    // refreshed toast up; the margin makes this timer fire after `until`.
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.until > Date.now())), TOAST_MS + 50);
  }, []);

  const dispatch = useCallback(
    (command: Command) => {
      // Orders to a unit the player only thinks is theirs (a disguised spy)
      // do nothing, and saying why would give it away: no feedback for them.
      const { real, rules, playerId } = frames.get();
      const fake = commandUnitIds(command).some((id) => !reallyOwns(real, playerId, id));
      const pending = fake ? null : trackCommand(real, command, rules, playerId);
      if (pending) {
        pendingRef.current.push(pending);
        const spend = commandSpend(real, rules, playerId, command);
        if (spend) spendRef.current.set(command, spend);
      }
      if (command.type === 'cancel') cancelled.add(command.buildingId);
      send(command);
    },
    [frames, send, cancelled]
  );

  useEffect(() => {
    if (pendingRef.current.length === 0) return;
    const { waiting, refused, placed, notes } = settleCommands(
      frame.real,
      frame.ctx,
      frame.playerId,
      pendingRef.current,
      claimedRef.current,
      // Fog-safe, so a refusal doesn't reveal a hidden city's distance.
      frame.knownBuilding
    );
    // Commands no longer waiting landed, unless any was refused this pass
    // (which can't be told apart here): flash only when none was.
    const still = new Set(waiting.map((p) => p.command));
    const settled = pendingRef.current.filter((p) => !still.has(p.command));
    if (refused.length === 0) {
      const spent: Record<string, number>[] = [];
      for (const p of settled) {
        const amounts = spendRef.current.get(p.command);
        if (amounts) spent.push(amounts);
      }
      if (spent.length > 0) flashSpend(sumSpends(spent));
    }
    for (const p of settled) spendRef.current.delete(p.command);
    pendingRef.current = waiting;
    if (refused.length === 0 && placed.length === 0 && notes.length === 0) return;
    pushToasts([
      ...placed.map((text) => ({ text, tone: 'ok' as const })),
      ...[...notes, ...refused].map((text) => ({ text, tone: 'warn' as const })),
    ]);
  }, [frame, pushToasts, flashSpend]);

  // Settlers sent to found a city on arrival (`foundAt`): one that stops
  // without founding says why, however long the walk took.
  const foundWatchRef = useRef({ tick: 0, orders: new Map<string, HexTileCoordinates>() });
  useEffect(() => {
    const { real: state, ctx, playerId, cityNumbers } = frame;
    const dropped = droppedFoundOrders(
      state,
      ctx,
      playerId,
      foundWatchRef.current.orders,
      frame.knownBuilding,
      (id) => {
        const city = state.buildings[id];
        return city ? cityName(city, playerId, cityNumbers, frame.playerName, frame.names) : null;
      },
      foundWatchRef.current.tick
    );
    foundWatchRef.current = { tick: state.tick, orders: foundOrders(state, playerId) };
    if (dropped.length > 0) pushToasts(dropped.map((text) => ({ text, tone: 'warn' as const })));
  }, [frame, pushToasts]);

  return { dispatch, toasts, pushToasts, cancelled, spends };
}
